import React, { ReactNode } from 'react';
import { RouterProvider, Navigate, createBrowserRouter, useParams } from 'react-router-dom';
import { useAuth } from '@/lib/hooks/use-auth';
import Login from '@/pages/auth/login';
import Register from '@/pages/auth/register';
import ForgotPassword from '@/pages/auth/forgot-password';
import InviteNewUser from '@/pages/auth/invite-new-user';
import InviteUser from '@/pages/auth/invite-user';
import TokenVerification from '@/pages/auth/token-verification';
import ResetPassword from '@/pages/auth/reset-password';
import Home from '@/pages/home';
import Users from '@/pages/users/list-users';
import UserProfile from '@/pages/users/user-profile';
import EditUser from '@/pages/users/edit-user';
import ListProducts from '@/pages/workspace/list-products';
import Settings from '@/pages/workspace/settings';
import EditAccount from '@/pages/workspace/edit-account';
import ListFields from '@/pages/fields/list-fields';
import CreateField from '@/pages/fields/create-field';
import EditField from '@/pages/fields/edit-field';
import ListContentLibrary from '@/pages/libraries/list-content-library';
import CreateContentLibrary from '@/pages/libraries/create-content-library';
import EditContentLibrary from '@/pages/libraries/edit-content-library';
import Contents from '@/pages/contents/contents';
import CreateContent from '@/pages/contents/create-content';
import EditContent from '@/pages/contents/edit-content';
import PublicContent from '@/pages/contents/public-content';
import PublicContentList from '@/pages/contents/public-content-list';
import CancelSubscription from '@/pages/workspace/cancel-subscription';

import MainLayout from '@/pages/layout/layout';
import AuthLayout from '@/pages/layout/auth';
import PublicLayout from '@/pages/layout/public';
import LimitLayout from '@/pages/layout/limited';
import LimitedHome from '@/pages/limited-home';

import ListWorkspace from '@/pages/workspace/list-workspace';
import ResumeSubscription from '@/pages/workspace/resume-subscription';

import ListCollectionType from './pages/collection-types/list-collection-type';
import CreateCollectionType from './pages/collection-types/create-collection-type';
import EditCollectionType from './pages/collection-types/edit-collection-type';
import Collections from './pages/collections/collections';
import CreateCollection from './pages/collections/create-collection';
import EditCollection from './pages/collections/edit-collection';
import PublicCollection from './pages/collections/public-collection';
import PublicCollectionList from './pages/collections/public-collection-list';

import Calendar from './pages/calendar/calendar';

import {
  userProfileLoader,
  usersLoader,
  userLoader,
  productsLoader,
  fieldLoader,
  fieldsLoader,
  contentFieldLoader,
  contentLoader,
  sportTypesLoader,
} from '@/utils/loader';
import useIsCancelled from '@/lib/hooks/use-is-cancelled';
import useWorkspace from '@/lib/hooks/use-workspace';

interface RouteProps {
  children: ReactNode;
}

const ProtectedRoute: React.FC<RouteProps> = ({ children }) => {
  const isCancelled = useIsCancelled();
  const { workspaceId } = useParams();
  const auth = useAuth();

  if (!auth.isAuthenticated) {
    return <Navigate to='/auth/login' replace />;
  }

  if (isCancelled) {
    return <Navigate to={`/limited/${workspaceId}/home`} replace />;
  }

  return children;
};

const LimitedRoute: React.FC<RouteProps> = ({ children }) => {
  const auth = useAuth();

  if (!auth.isAuthenticated) {
    return <Navigate to='/auth/login' replace />;
  }

  return children;
};

const PublicRoute: React.FC<RouteProps> = ({ children }) => {
  const auth = useAuth();
  const { workspace } = useWorkspace();

  if (auth.isAuthenticated && workspace) {
    return <Navigate to={`/${workspace.id}/home`} replace />;
  }

  return children;
};

const router = createBrowserRouter([
  {
    path: '/',
    element: <Navigate to='/auth/login' replace />, // Redirect to login
  },
  {
    path: '/auth',
    element: (
      <PublicRoute>
        <AuthLayout />
      </PublicRoute>
    ),
    children: [
      {
        path: 'login',
        element: <Login />,
      },
      {
        path: 'register',
        loader: sportTypesLoader,
        element: <Register />,
      },
      {
        path: 'forgot-password',
        element: <ForgotPassword />,
      },
      {
        path: 'reset-password/:token',
        element: <ResetPassword />,
      },
      {
        path: 'verify-email/:token',
        element: <TokenVerification />,
      },
      {
        path: 'invite-new-user/:token',
        element: <InviteNewUser />,
      },
      {
        path: 'invite-user/:token',
        element: <InviteUser />,
      },
    ],
  },
  {
    path: ':workspaceId',
    element: (
      <ProtectedRoute>
        <MainLayout />
      </ProtectedRoute>
    ),
    children: [
      {
        path: 'home',
        element: <Home />,
      },
      { path: 'settings', element: <Settings /> },
      { path: 'users', loader: usersLoader, element: <Users /> },
      {
        path: 'users/:userId/edit',
        loader: userLoader,
        element: <EditUser />,
      },
      {
        path: 'my/profile',
        loader: userProfileLoader,
        element: <UserProfile />,
      },
      {
        path: 'billing',
        loader: productsLoader,
        element: <ListProducts />,
      },
      {
        path: 'account/edit',
        element: <EditAccount />,
      },
      {
        path: 'fields',
        loader: fieldsLoader,
        element: <ListFields />,
      },
      {
        path: 'fields/create/:fieldType',
        element: <CreateField />,
      },
      {
        path: 'fields/:id/edit',
        loader: fieldLoader,
        element: <EditField />,
      },
      {
        path: 'content-types',
        element: <ListContentLibrary />,
      },
      {
        path: 'content-types/create',
        element: <CreateContentLibrary />,
      },
      {
        path: 'content-types/:id/edit',
        element: <EditContentLibrary />,
      },
      {
        path: 'content-types/:contentTypeId/contents/:tabIndex',
        element: <Contents />,
      },
      {
        path: 'content-types/:contentTypeId/contents/create',
        loader: contentFieldLoader,
        element: <CreateContent />,
      },
      {
        path: 'content-types/:contentTypeId/contents/:id/edit',
        loader: contentFieldLoader,
        element: <EditContent />,
      },
      {
        path: 'workspaces',
        element: <ListWorkspace />,
      },
      {
        path: 'cancel-subscription',
        element: <CancelSubscription />,
      },
      {
        path: 'collection-types',
        element: <ListCollectionType />,
      },
      {
        path: 'collection-types/create',
        element: <CreateCollectionType />,
      },
      {
        path: 'collection-types/:id/edit',
        element: <EditCollectionType />,
      },
      {
        path: 'collection-types/:collectionTypeId/collections/:tabIndex',
        element: <Collections />,
      },
      {
        path: 'collection-types/:collectionTypeId/collections/create',
        element: <CreateCollection />,
      },
      {
        path: 'collection-types/:collectionTypeId/collections/:id/edit',
        element: <EditCollection />,
      },
      {
        path: 'calendar/:calendarType',
        element: <Calendar />,
      },
    ],
  },
  {
    path: '/limited',
    element: (
      <LimitedRoute>
        <LimitLayout />
      </LimitedRoute>
    ),
    children: [
      {
        path: ':workspaceId/home',
        element: <LimitedHome />,
      },
      {
        path: ':workspaceId/workspaces',
        element: <ListWorkspace />,
      },
      {
        path: ':workspaceId/resume-subscription',
        element: <ResumeSubscription />,
      },
    ],
  },
  {
    path: '/public',
    element: (
      <PublicRoute>
        <PublicLayout />
      </PublicRoute>
    ),
    children: [
      {
        path: 'content/:referenceId',
        loader: contentLoader,
        element: <PublicContent />,
      },
      {
        path: 'content-categories/:referenceId',
        element: <PublicContentList />,
      },
      {
        path: 'collections/:referenceId',
        element: <PublicCollection />,
      },
      {
        path: 'collection-types/:referenceId',
        element: <PublicCollectionList />,
      },
    ],
  },
]);

const App: React.FC = () => {
  return <RouterProvider router={router} />;
};

export default App;
